import * as React from "react";
import {
    Button,
    Checkbox,
    Input,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    Select,
    Toast,
    ToastTitle,
    Toaster,
    useId,
    useToastController,
} from "@fluentui/react-components";
import { Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "@fluentui/react-table";
import {
    Add24Regular,
    ClockBill20Regular,
    Delete24Regular,
    Dismiss24Regular,
    Edit24Regular,
    Mail24Regular,
    Phone24Regular,
    PhoneDesktop24Regular,
    Print24Regular,
} from "@fluentui/react-icons";
import { SocialIcon } from "react-social-icons";
import { apiUrl } from "../../utils/api";
import { useTelephony } from "../../context/TelephonyContext";
import { Communication } from "./types";

type CommunicationManagementSectionProps = {
    contactId: number;
    communications: Communication[];
    erreichbarkeit?: string;
    onUpdateErreichbarkeit?: (value: string) => void;
    title?: string;
};

export const CommunicationManagementSection: React.FC<CommunicationManagementSectionProps> = ({
    contactId,
    communications,
    erreichbarkeit,
    onUpdateErreichbarkeit,
    title = "Kommunikation",
}) => {
    const { callNumber } = useTelephony();
    const [commList, setCommList] = React.useState<Communication[]>(communications);
    const [selectedComm, setSelectedComm] = React.useState<Communication | null>(null);

    const [commType, setCommType] = React.useState<Communication["type"]>("phone");
    const [commLabel, setCommLabel] = React.useState("");
    const [commValue, setCommValue] = React.useState("");
    const [commDefault, setCommDefault] = React.useState(false);

    const [erreichbarkeitValue, setErreichbarkeitValue] = React.useState(erreichbarkeit || "");
    const [isErreichbarkeitOpen, setIsErreichbarkeitOpen] = React.useState(false);
    const [isSavingErreichbarkeit, setIsSavingErreichbarkeit] = React.useState(false);

    const toasterId = useId("communication-toaster");
    const { dispatchToast } = useToastController(toasterId);

    React.useEffect(() => {
        setCommList(communications);
    }, [communications]);

    React.useEffect(() => {
        setErreichbarkeitValue(erreichbarkeit || "");
    }, [erreichbarkeit]);

    React.useEffect(() => {
        if (selectedComm) {
            setCommType(selectedComm.type);
            setCommLabel(selectedComm.label || "");
            setCommValue(selectedComm.value || "");
            setCommDefault(selectedComm.isDefault);
            return;
        }

        setCommType("phone");
        setCommLabel("");
        setCommValue("");
        setCommDefault(false);
    }, [selectedComm]);

    const handleSaveErreichbarkeit = async () => {
        setIsSavingErreichbarkeit(true);

        try {
            const response = await fetch(apiUrl(`contact/${contactId}`), {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ erreichbarkeit: erreichbarkeitValue }),
            });

            if (!response.ok) {
                throw new Error("Failed to save erreichbarkeit");
            }

            onUpdateErreichbarkeit?.(erreichbarkeitValue);
            setIsErreichbarkeitOpen(false);
            dispatchToast(<Toast><ToastTitle>Erreichbarkeit gespeichert</ToastTitle></Toast>, { intent: "success" });
        } catch (error) {
            console.error("Error saving erreichbarkeit", error);
            dispatchToast(<Toast><ToastTitle>Fehler beim Speichern</ToastTitle></Toast>, { intent: "error" });
        } finally {
            setIsSavingErreichbarkeit(false);
        }
    };

    const handleNewComm = () => {
        setSelectedComm({
            id: Date.now(),
            type: "phone",
            label: "",
            value: "",
            isDefault: false,
        });
    };

    const handleSaveComm = async () => {
        if (!selectedComm) {
            return;
        }

        try {
            const payload = {
                type: commType,
                label: commLabel,
                value: commValue,
                isDefault: commDefault,
            };

            const isNew = !communications.some((comm) => comm.id === selectedComm.id);

            if (isNew) {
                const response = await fetch(apiUrl(`contact/${contactId}/communication`), {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });

                const newComm = await response.json();
                setCommList((prev) => [...prev, newComm]);
            } else {
                const response = await fetch(apiUrl(`contact/${contactId}/communication/${selectedComm.id}`), {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });

                const updatedComm = await response.json();
                setCommList((prev) => prev.map((comm) => (comm.id === selectedComm.id ? updatedComm : comm)));
            }

            setSelectedComm(null);
            dispatchToast(<Toast><ToastTitle>Kommunikation gespeichert</ToastTitle></Toast>, { intent: "success" });
        } catch (error) {
            console.error("Error saving communication:", error);
            dispatchToast(<Toast><ToastTitle>Fehler beim Speichern</ToastTitle></Toast>, { intent: "error" });
        }
    };

    const handleDeleteComm = async (id: number) => {
        try {
            await fetch(apiUrl(`contact/${contactId}/communication/${id}`), {
                method: "DELETE",
            });

            setCommList((prev) => prev.filter((comm) => comm.id !== id));
            if (selectedComm?.id === id) {
                setSelectedComm(null);
            }
            dispatchToast(<Toast><ToastTitle>Kommunikation gelöscht</ToastTitle></Toast>, { intent: "success" });
        } catch (error) {
            console.error("Error deleting communication:", error);
            dispatchToast(<Toast><ToastTitle>Fehler beim Löschen</ToastTitle></Toast>, { intent: "error" });
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Toaster toasterId={toasterId} />
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <h3>{title}</h3>
                    <Popover open={isErreichbarkeitOpen} onOpenChange={(_, data) => setIsErreichbarkeitOpen(data.open)}>
                        <PopoverTrigger disableButtonEnhancement>
                            <Button icon={<ClockBill20Regular />} appearance="subtle" title={erreichbarkeitValue || "Erreichbarkeit hinzufügen"} />
                        </PopoverTrigger>
                        <PopoverSurface style={{ padding: "12px", width: "300px" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                <Input
                                    value={erreichbarkeitValue}
                                    onChange={(_, data) => setErreichbarkeitValue(data.value)}
                                    placeholder="Erreichbarkeit (z.B. Mo-Fr 9-17 Uhr)"
                                    style={{ width: "100%" }}
                                />
                                <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                                    <Button size="small" onClick={() => setIsErreichbarkeitOpen(false)}>Abbrechen</Button>
                                    <Button
                                        size="small"
                                        appearance="primary"
                                        onClick={handleSaveErreichbarkeit}
                                        disabled={isSavingErreichbarkeit}
                                    >
                                        {isSavingErreichbarkeit ? "Lädt..." : "Speichern"}
                                    </Button>
                                </div>
                            </div>
                        </PopoverSurface>
                    </Popover>
                </div>
                <Button icon={<Add24Regular />} onClick={handleNewComm}>Neu</Button>
            </div>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell style={{ width: "60px" }}>Typ</TableHeaderCell>
                        <TableHeaderCell>Bezeichnung</TableHeaderCell>
                        <TableHeaderCell>Nummer/E-Mail</TableHeaderCell>
                        <TableHeaderCell style={{ width: "140px" }}>Aktion</TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {[...commList]
                        .filter((comm) => !["linkedin", "instagram", "facebook", "tiktok", "homepage"].includes(comm.type))
                        .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))
                        .map((comm) => (
                            <TableRow
                                key={comm.id}
                                onClick={() => setSelectedComm(comm)}
                                style={{ cursor: "pointer", background: selectedComm?.id === comm.id ? "#f0f6ff" : undefined }}
                            >
                                <TableCell>
                                    {comm.type === "phone" && <Phone24Regular />}
                                    {comm.type === "mobile" && <PhoneDesktop24Regular />}
                                    {comm.type === "fax" && <Print24Regular />}
                                    {comm.type === "email" && <Mail24Regular />}
                                    {["linkedin", "instagram", "facebook", "tiktok", "homepage"].includes(comm.type) && (
                                        <SocialIcon network={comm.type === "homepage" ? undefined : comm.type} style={{ height: 20, width: 20 }} />
                                    )}
                                </TableCell>
                                <TableCell>{comm.label}</TableCell>
                                <TableCell>{comm.value}</TableCell>
                                <TableCell>
                                    {(comm.type === "phone" || comm.type === "mobile") && (
                                        <Phone24Regular
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`${comm.value} anrufen`}
                                            style={{ cursor: "pointer", marginRight: 8 }}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                callNumber(comm.value);
                                            }}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    callNumber(comm.value);
                                                }
                                            }}
                                        />
                                    )}
                                    {comm.type === "email" && (
                                        <Mail24Regular
                                            style={{ cursor: "pointer", marginRight: 8 }}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                window.location.href = `mailto:${comm.value}`;
                                            }}
                                        />
                                    )}
                                    <Edit24Regular style={{ cursor: "pointer", marginRight: 8 }} onClick={(event) => { event.stopPropagation(); setSelectedComm(comm); }} />
                                    <Delete24Regular style={{ cursor: "pointer" }} onClick={(event) => { event.stopPropagation(); handleDeleteComm(comm.id); }} />
                                </TableCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>

            {selectedComm && (
                <div style={{ marginTop: 20, padding: 20, border: "1px solid #ccc", borderRadius: 4, position: "relative" }}>
                    <Dismiss24Regular style={{ position: "absolute", top: 10, right: 10, cursor: "pointer" }} onClick={() => setSelectedComm(null)} />
                    <h4>Kommunikation bearbeiten</h4>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                        <Select value={commType} onChange={(_, data) => setCommType(data.value as Communication["type"])}>
                            <option value="phone">Telefon</option>
                            <option value="mobile">Mobil</option>
                            <option value="fax">Fax</option>
                            <option value="email">E-Mail</option>
                        </Select>
                        <Input value={commLabel} onChange={(_, data) => setCommLabel(data.value)} placeholder="Bezeichnung" />
                        <Input value={commValue} onChange={(_, data) => setCommValue(data.value)} placeholder="Wert" style={{ gridColumn: "1 / -1" }} />
                    </div>
                    <Checkbox checked={commDefault} onChange={(_, data) => setCommDefault(data.checked === true)} label="Als Standard für diesen Typ festlegen" style={{ marginTop: 10 }} />
                    <Button appearance="primary" onClick={handleSaveComm} style={{ marginTop: 10 }}>Speichern</Button>
                </div>
            )}
        </div>
    );
};
